import { Component, OnInit, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import {
  ChatbotService,
  ChatbotMessage,
  ConversationSummary,
  ChatMessageResponse
} from 'src/app/services/chatbot.service';

interface UiMessage {
  role: 'USER' | 'ASSISTANT';
  content: string;
  createdAt: Date;
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, AfterViewChecked {

  @ViewChild('scrollContainer') private scrollContainer!: ElementRef<HTMLDivElement>;

  messages: UiMessage[] = [];
  conversations: ConversationSummary[] = [];
  currentConversationId: number | null = null;

  inputText: string = '';
  loading: boolean = false;
  escalated: boolean = false;

  private shouldScroll: boolean = false;

  constructor(private chatbotService: ChatbotService) {}

  ngOnInit(): void {
    this.loadConversations();
    this.showWelcome();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll && this.scrollContainer) {
      this.scrollContainer.nativeElement.scrollTop = this.scrollContainer.nativeElement.scrollHeight;
      this.shouldScroll = false;
    }
  }

  private showWelcome(): void {
    this.messages = [{
      role: 'ASSISTANT',
      content: 'Hola, soy el asistente de soporte de Nectum. ¿En qué puedo ayudarte?',
      createdAt: new Date()
    }];
    this.shouldScroll = true;
  }

  loadConversations(): void {
    this.chatbotService.listConversations().subscribe({
      next: (list) => { this.conversations = list; },
      error: (err) => { console.error('Error cargando conversaciones:', err); }
    });
  }

  openConversation(conv: ConversationSummary): void {
    this.currentConversationId = conv.id;
    this.escalated = conv.escalated;
    this.loading = true;
    this.chatbotService.getMessages(conv.id).subscribe({
      next: (msgs: ChatbotMessage[]) => {
        this.messages = msgs
          .filter(m => m.role !== 'SYSTEM')
          .map(m => ({
            role: m.role as 'USER' | 'ASSISTANT',
            content: m.content,
            createdAt: new Date(m.createdAt)
          }));
        this.shouldScroll = true;
        this.loading = false;
      },
      error: (err) => {
        console.error('Error cargando mensajes:', err);
        this.loading = false;
      }
    });
  }

  newConversation(): void {
    this.currentConversationId = null;
    this.escalated = false;
    this.showWelcome();
  }

  send(): void {
    const text = this.inputText.trim();
    if (!text || this.loading) return;

    this.messages.push({ role: 'USER', content: text, createdAt: new Date() });
    this.inputText = '';
    this.loading = true;
    this.shouldScroll = true;

    this.chatbotService.sendMessage({
      conversationId: this.currentConversationId,
      message: text
    }).subscribe({
      next: (res: ChatMessageResponse) => {
        this.currentConversationId = res.conversationId;
        this.messages.push({
          role: 'ASSISTANT',
          content: res.assistantMessage,
          createdAt: new Date(res.createdAt)
        });
        this.loading = false;
        this.shouldScroll = true;
        this.loadConversations();
      },
      error: (err) => {
        console.error('Error enviando mensaje:', err);
        this.messages.push({
          role: 'ASSISTANT',
          content: 'Lo siento, hubo un problema. Inténtalo de nuevo o escala a soporte humano.',
          createdAt: new Date()
        });
        this.loading = false;
        this.shouldScroll = true;
      }
    });
  }

  escalate(): void {
    if (!this.currentConversationId || this.escalated) return;
    this.chatbotService.escalate(this.currentConversationId).subscribe({
      next: () => {
        this.escalated = true;
        this.messages.push({
          role: 'ASSISTANT',
          content: 'Tu conversación ha sido enviada a soporte humano. Te contactarán por correo.',
          createdAt: new Date()
        });
        this.shouldScroll = true;
      },
      error: (err) => { console.error('Error escalando:', err); }
    });
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }
}

